// import components
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

// import hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import types
import { GridColDef } from '@mui/x-data-grid-pro';
import { TravelMovementPerDayResponse } from 'generated/index';

// import utils
import { dateFormatter } from 'utils/grid';
import {
  calculateCompensationInEuros,
  NumberIsFinite,
  sumInEuros
} from 'components/roster-portal/home/travel-expense-claims/utils';

export const useColumnDefinitions = ({
  showEmployeeName = false
}: {
  showEmployeeName?: boolean;
}) => {
  const { t } = useTranslation('common');

  return useMemo(
    () =>
      [
        {
          field: 'employmentNumber',
          type: 'string',
          headerName: t('travelMovements.employmentNumber'),
          valueGetter: (_, row: TravelMovementPerDayResponse) =>
            row?.employment?.number ?? '',
          width: 150
        },
        ...(showEmployeeName
          ? [
              {
                field: 'employeeName',
                type: 'string',
                headerName: t('employee'),
                valueGetter: (_, row: TravelMovementPerDayResponse) =>
                  row?.employment?.employeeName ?? '',
                width: 120,
                flex: 1
              }
            ]
          : []),
        {
          field: 'date',
          type: 'date',
          headerName: t('date'),
          valueFormatter: dateFormatter,
          width: 120
        },
        {
          field: 'travelDistancePrivate',
          headerName: t('travelMovements.distancePrivate'),
          valueGetter: (_, row: TravelMovementPerDayResponse) => {
            const routes = row.travelMovements || [];
            const distanceInMeters = routes.reduce(
              (acc, route) =>
                route.type === 'PRIVATE'
                  ? acc +
                    (NumberIsFinite(route.correctedDistanceInMeters)
                      ? route.correctedDistanceInMeters!
                      : route.distanceInMeters || 0)
                  : acc,
              0
            );
            return distanceInMeters;
          },
          valueFormatter: (value) => {
            if (!value) return '';

            const distanceInKm = value / 1000;
            return Number.isInteger(distanceInKm)
              ? distanceInKm
              : distanceInKm.toFixed(2).replace('.', ',');
          },
          type: 'number',
          width: 150,
          align: 'left',
          headerAlign: 'left'
        },
        {
          field: 'travelDistanceBusiness',
          headerName: t('travelMovements.distanceBusiness'),
          valueGetter: (_, row: TravelMovementPerDayResponse) => {
            const routes = row.travelMovements || [];
            const isManual = row.isManual;
            const commuteDistanceInMeters = row.commuteDistanceInMeters || 0;

            const manualDistanceInMeters = isManual
              ? routes.reduce(
                  (acc, route) =>
                    row.isManual && route.type === 'BUSINESS'
                      ? acc +
                        (route.correctedDistanceInMeters
                          ? route.correctedDistanceInMeters
                          : route.distanceInMeters || 0)
                      : acc,
                  0
                )
              : 0;

            const distanceInMeters = isManual
              ? Math.max(manualDistanceInMeters - commuteDistanceInMeters, -1)
              : routes.reduce(
                  (acc, route) =>
                    route.type === 'BUSINESS'
                      ? acc +
                        (route.correctedDistanceInMeters
                          ? route.correctedDistanceInMeters
                          : route.distanceInMeters || 0)
                      : acc,
                  0
                );
            return distanceInMeters;
          },
          valueFormatter: (value) => {
            if (!value) return '';
            if (value === -1) return '0';
            const distanceInKm = value / 1000;
            return Number.isInteger(distanceInKm)
              ? distanceInKm
              : distanceInKm.toFixed(2).replace('.', ',');
          },
          type: 'number',
          width: 150,
          align: 'left',
          headerAlign: 'left'
        },
        {
          field: 'transportationMode',
          type: 'string',
          headerName: t('travelDistances.transportationMode'),
          valueGetter: (_, row: TravelMovementPerDayResponse) => {
            const mode = row.transportationMode || '';
            return t(`travelDistances.transportationModes.${mode}`);
          },
          width: 140
        },
        {
          field: 'declarationType',
          type: 'string',
          headerName: t('travelMovements.declarationType'),
          valueGetter: (_, row: TravelMovementPerDayResponse) =>
            row?.declarationType?.name ?? '',
          width: 120
        },
        {
          field: 'description',
          type: 'string',
          headerName: t('description'),
          width: 120
        },
        {
          field: 'statusNote',
          type: 'string',
          headerName: t('travelExpenseClaims.statusNote'),
          width: 120
        },
        {
          field: 'currentStatus',
          type: 'string',
          headerName: t('status'),
          valueGetter: (_, row: TravelMovementPerDayResponse) =>
            t(`travelMovements.status.${row.status ?? ''}`),
          minWidth: 90,
          width: 100
        },
        {
          field: 'amountInEuros',
          type: 'number',
          headerName: t('travelDistances.compensation'),
          valueGetter: (_, row: TravelMovementPerDayResponse) => {
            const routes = row.travelMovements || [];
            const compensation = calculateCompensationInEuros(routes, true);
            return compensation || '';
          },
          valueFormatter: (value) => value && `${sumInEuros(value)}`,
          width: 100,
          headerAlign: 'left',
          align: 'left'
        },
        {
          field: 'isManual',
          type: 'string',
          headerName: t('travelMovements.isManual'),
          width: 100,
          renderCell: BooleanCell
        },
        {
          field: 'hasFixedDistances',
          type: 'string',
          headerName: t('travelMovements.fixedKilometers'),
          width: 100,
          renderCell: BooleanCell
        }
      ] as GridColDef[],
    [t, showEmployeeName]
  );
};

export default useColumnDefinitions;
